import React, { useState, useEffect } from 'react'; import { ethers } from 'ethers'; import { useWallet } from '../context/WalletContext'; const Dashboard = () => {
    const { readOnlyTokenContract, readOnlyMiningContract, account } = useWallet();
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminChecked, setAdminChecked] = useState(false);
    const [stats, setStats] = useState({
        contractBalance: '0.0000000 HT',
        totalUsers: '0',
        totalDeposit: '0.0000000 BNB',
        staticRewards: '0.0000000 HT',
        dynamicRewards: '0.0000000 HT',
        totalDividendDistributed: '0.0000000 BNB',
        totalOrderAmount: '0.0000000 USDT',
        dividendPool: '0.0000000 BNB',
    });
    const [snapshots, setSnapshots] = useState([]);

    const formatDisplayValue = (val, unit) => {
        if (!val) return "0.0000000 " + unit;
        try {
            return Number(val).toFixed(7) + " " + unit;
        } catch (e) {
            return "0.0000000 " + unit;
        }
    };

    const formatHourlyRateStr = (rate) => {
        if (!rate || rate === 0) return "0.00%";

        // hourlyRate 使用2位小数精度（例如542表示5.42基点）
        // 基点 = rate / 100
        // 百分比 = 基点 / 100
        const basisPoints = Number(rate) / 100;
        const percentage = basisPoints / 100;

        // 计算对应的日收益率
        const dailyRate = percentage * 24;

        return `${dailyRate.toFixed(2)}%`;
    };

    useEffect(() => {
        const init = async () => {
            if (!readOnlyMiningContract || !account) {
                setIsAdmin(false);
                setAdminChecked(true);
                setLoading(false);
                return;
            }
            try {
                const adminRole = await readOnlyMiningContract.ADMIN_ROLE();
                const hasAdminRole = await readOnlyMiningContract.hasRole(adminRole, account);
                setIsAdmin(hasAdminRole);
                if (hasAdminRole && readOnlyTokenContract) {
                    await loadData();
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error checking admin status:", error);
                setIsAdmin(false);
                setLoading(false);
            } finally {
                setAdminChecked(true);
            }
        };
        init();
    }, [readOnlyTokenContract, readOnlyMiningContract, account]);

    const loadData = async () => {
        try {
            setLoading(true);

            const balance = await readOnlyTokenContract
                .balanceOf(readOnlyMiningContract.target || readOnlyMiningContract.address)
                .catch((err) => {
                    console.error("balanceOf error:", err);
                    return 0;
                });

            const globalData = await readOnlyMiningContract.globalData().catch((err) => {
                console.error("globalData error:", err);
                return null;
            });

            if (globalData) {
                setStats({
                    contractBalance: formatDisplayValue(ethers.formatEther(balance), "HT"),
                    totalUsers: String(globalData[1] || globalData.totalUsers || 0),
                    totalDeposit: formatDisplayValue(ethers.formatEther(globalData[0] || globalData.totalDeposit || 0), "BNB"),
                    staticRewards: formatDisplayValue(ethers.formatEther(globalData[2] || globalData.totalStaticPaid || 0), "HT"),
                    dynamicRewards: formatDisplayValue(ethers.formatEther(globalData[3] || globalData.totalDynamicPaid || 0), "HT"),
                    totalDividendDistributed: formatDisplayValue(ethers.formatEther(globalData[4] || globalData.totalDividendDistributed || 0), "BNB"),
                    totalOrderAmount: formatDisplayValue(ethers.formatEther(globalData[5] || globalData.totalOrderAmount || 0), "USDT"),
                    dividendPool: formatDisplayValue(ethers.formatEther(globalData[6] || globalData.dividendPool || 0), "BNB"),
                });
            }

            // 新的快照加载逻辑：基于时间戳间隔，仅读取最近 5 个
            const SNAPSHOT_INTERVAL = 7200; // 2小时
            const currentTime = Math.floor(Date.now() / 1000);
            const currentInterval = Math.floor(currentTime / SNAPSHOT_INTERVAL);

            const snapshotData = [];
            // 尝试回溯扫描最近的 50 个间隔，直到找到 5 个有效快照
            const lookbackRange = 50;
            const scanStart = currentInterval - lookbackRange;

            // 改为完全串行异步加载，一个接一个读取，直到找齐 5 个
            // 这样能最大限度避免触发 RPC 的并发限制 (429/509)
            for (let i = currentInterval; i >= scanStart && snapshotData.length < 5; i--) {
                const timestamp = i * SNAPSHOT_INTERVAL;
                try {
                    const snapshot = await readOnlyMiningContract.snapshots(timestamp);
                    if (snapshot) {
                        let price, bnbPrice, hourlyRate, exists;
                        try {
                            price = snapshot[0];
                            bnbPrice = snapshot[1];
                            hourlyRate = snapshot[2];
                            exists = snapshot[3];

                            if (price === undefined || typeof price === 'boolean') {
                                price = snapshot.price;
                                bnbPrice = snapshot.bnbPrice;
                                hourlyRate = snapshot.hourlyRate;
                                exists = snapshot.exists;
                            }
                        } catch (e) { }

                        const hasPrice = price && (typeof price === 'bigint' ? price > 0n : Number(price) > 0);
                        if (exists === true || hasPrice) {
                            snapshotData.push({
                                id: timestamp,
                                timestamp: timestamp,
                                date: new Date(timestamp * 1000).toLocaleString(),
                                price: formatDisplayValue(ethers.formatUnits(price || 0, 18), ""),
                                bnbPrice: formatDisplayValue(ethers.formatUnits(bnbPrice || 0, 18), ""),
                                rateInfo: formatHourlyRateStr(hourlyRate),
                            });
                        }
                    }
                } catch (err) {
                    console.warn(`Snapshot call failed at ${timestamp}:`, err.message);
                }

                // 每次请求后稍微停顿 100ms，确保 RPC 压力极小
                if (snapshotData.length < 5 && i > scanStart) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            setSnapshots(snapshotData); // 已经是按时间降序排列的
        } catch (error) {
            console.error("Error loading dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    if (!adminChecked || loading) {
        return <div className="loading">加载中...</div>;
    }

    if (!isAdmin) {
        return (
            <div className="card">
                <h2 className="card-title">仪表盘</h2>
                <p style={ { color: "#ef4444", textAlign: "center", padding: "20px" } }>
                    您没有管理员权限
                </p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="stats-grid">
                <div className="stat-item">
                    <div className="stat-label">合约余额</div>
                    <div className="stat-value">{ stats.contractBalance }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">总用户数</div>
                    <div className="stat-value">{ stats.totalUsers }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">总质押量</div>
                    <div className="stat-value">{ stats.totalDeposit }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">累计分红(BNB)</div>
                    <div className="stat-value">{ stats.totalDividendDistributed }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">静态产出</div>
                    <div className="stat-value">{ stats.staticRewards }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">动态产出</div>
                    <div className="stat-value">{ stats.dynamicRewards }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">空单+锁仓单总额</div>
                    <div className="stat-value">{ stats.totalOrderAmount }</div>
                </div>
                <div className="stat-item">
                    <div className="stat-label">分红池余额</div>
                    <div className="stat-value">{ stats.dividendPool }</div>
                </div>
            </div>

            <h2 className="card-title" style={ { marginTop: "20px" } }>
                价格快照
            </h2>
            <div className="snapshots-container">
                { snapshots.length > 0 ? (
                    snapshots.map((snapshot) => (
                        <div key={ snapshot.id } className="snapshot-card">
                            <div className="snapshot-time">{ snapshot.date }</div>
                            <div className="snapshot-data">
                                <div className="snapshot-item">
                                    <div className="snapshot-item-label">价格</div>
                                    <div className="snapshot-item-value">${ snapshot.price }</div>
                                </div>
                                <div className="snapshot-item">
                                    <div className="snapshot-item-label">BNB价格</div>
                                    <div className="snapshot-item-value">${ snapshot.bnbPrice }</div>
                                </div>
                                <div className="snapshot-item">
                                    <div className="snapshot-item-label">日化收益率</div>
                                    <div className="snapshot-item-value">
                                        { snapshot.rateInfo }
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="loading">暂无快照数据</div>
                ) }
            </div>
        </div>
    );
}; export default Dashboard;
